import React, { useRef, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import { styleTemplateFormMessages } from './styleTemplateForm.messages';
import {
  CreateStyleTemplateRequestSchema,
  MAX_STYLE_TEMPLATE_IMAGES,
} from 'schemas';

import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Heading3, Paragraph, Small } from '@/components/Typography';
import { ImagePreview } from '@/components/ImagePreview';
import { fileToDataUrl } from '@/helpers/file-manipulation/fileToDataUrl';
import { sharedMessages } from '@/shared-messages/shared.messages';

interface StyleTemplateFormProps {
  onSubmit: (formData: CreateStyleTemplateFormValues) => Promise<void>;
  isSubmitting?: boolean;
  onCancel?: () => void;
}

type CreateStyleTemplateFormValues = z.infer<
  typeof CreateStyleTemplateRequestSchema
>;

const MAX_IMAGES = MAX_STYLE_TEMPLATE_IMAGES;

export function StyleTemplateForm({
  onSubmit,
  isSubmitting = false,
  onCancel,
}: StyleTemplateFormProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [images, setImages] = useState<File[]>([]);
  const [imageUrls, setImageUrls] = useState<string[]>([]);

  const form = useForm<CreateStyleTemplateFormValues>({
    resolver: zodResolver(CreateStyleTemplateRequestSchema),
    defaultValues: {
      title: '',
      description: '',
      images: [],
    },
  });

  const handleFormSubmit = async (data: CreateStyleTemplateFormValues) => {
    // Convert images to base64 data URLs
    const imageDataUrls = await Promise.all(
      images.map((file) => fileToDataUrl(file))
    );

    const formDataWithImages = {
      ...data,
      images: imageDataUrls,
    };

    await onSubmit(formDataWithImages);
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(event.target.files || []);

    if (images.length + files.length > MAX_IMAGES) {
      alert(styleTemplateFormMessages.maxImagesReached);
      return;
    }

    const newImages = [...images, ...files];
    setImages(newImages);

    // Create object URLs for preview
    const newUrls = files.map((file) => URL.createObjectURL(file));
    setImageUrls([...imageUrls, ...newUrls]);

    // Update form value
    form.setValue(
      'images',
      newImages.map(() => 'placeholder')
    );
  };

  const removeImage = (index: number) => {
    const newImages = images.filter((_, i) => i !== index);
    const newUrls = imageUrls.filter((_, i) => i !== index);

    setImages(newImages);
    setImageUrls(newUrls);

    // Update form value
    form.setValue(
      'images',
      newImages.map(() => 'placeholder')
    );
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      imageUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [imageUrls]);

  return (
    <div className="mx-auto w-full">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleFormSubmit)}
          className="space-y-6"
          aria-label={styleTemplateFormMessages.formAriaLabel}
        >
          {/* Title Field */}
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{styleTemplateFormMessages.titleLabel}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={styleTemplateFormMessages.titlePlaceholder}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Description Field */}
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {styleTemplateFormMessages.descriptionLabel}
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder={
                      styleTemplateFormMessages.descriptionPlaceholder
                    }
                    rows={4}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Images Section */}
          <div className="space-y-4">
            <div>
              <Heading3 className="mb-1">
                {styleTemplateFormMessages.imagesLabel}
              </Heading3>
              <Paragraph className="text-muted-foreground">
                {styleTemplateFormMessages.imagesDescription}
              </Paragraph>
            </div>

            {/* Image Upload Area */}
            {images.length < MAX_IMAGES && (
              <div
                className="cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors hover:border-stone-400"
                onClick={openFileDialog}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleImageUpload}
                />

                <div className="space-y-4">
                  <div className="mx-auto h-12 w-12 text-stone-400">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Small>
                      {styleTemplateFormMessages.imageUploadPlaceholder}
                    </Small>
                    <Small className="mt-1">
                      {styleTemplateFormMessages.imageUploadSupportedFormats}
                    </Small>
                  </div>
                </div>
              </div>
            )}

            {/* Image Previews */}
            <ImagePreview
              images={images}
              imageUrls={imageUrls}
              onRemoveImage={removeImage}
            />

            {images.length >= MAX_IMAGES && (
              <Small className="text-center">
                {styleTemplateFormMessages.maxImagesReached}
              </Small>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex gap-4 pt-6">
            <Button
              type="submit"
              disabled={isSubmitting || !form.formState.isValid}
              className="flex-1"
            >
              {isSubmitting
                ? styleTemplateFormMessages.creating
                : styleTemplateFormMessages.createTemplateButton}
            </Button>
            {onCancel && (
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                {sharedMessages.commonButtons.cancel}
              </Button>
            )}
          </div>
        </form>
      </Form>
    </div>
  );
}
